import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import RouterPromptStay from 'app/router/RouterPromptStay'
import { ValidationErrors } from 'final-form'
import { useFlags } from 'launchdarkly-react-client-sdk'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'

import useGetBrandVoicesQuery from 'common/api/queries/useGetBrandVoicesQuery'
import { parseFieldsConfiguration } from 'common/components/dynamicFields/helpers'
import {
  ConditionalValue,
  FieldType,
} from 'common/components/dynamicFields/interfaces'
import Tabs from 'common/components/tabs/Tabs'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { mapBEMergeTag } from 'features/admin/accountSettings/api/helpers'
import useGenerateVariantsMutation from 'features/unifiedFlow/api/mutations/useGenerateVariantsMutation'
import PageDetails from 'features/unifiedFlow/contentPage/components/PageDetails'
import { userInputFormHasChanged } from 'features/unifiedFlow/store/unifiedFlowSlice'
import { selectTopics } from 'features/unifiedFlow/store/unifiedFlowSlice'

import {
  getAllTemplateElements,
  getIsOptimized,
  getStepStatus,
  hasOneElement,
  validateWorkflowName,
} from '../../helpers'
import { useContent, useSelectedElement, useTopics } from '../../hooks'
import useCheckVariants from '../hooks/useCheckVariants'

import { useBriefGeneration } from './hooks/useBriefGeneration'
import useIsBrazeContent from './hooks/useIsBrazeContent'
import { useWorkflowName } from './hooks/useWorkflowName'
import ChangesOverlay from './ChangesOverlay'
import DynamicForm from './DynamicForm'
import FormLeaveModal from './FormLeaveModal'
import GeneratedBrief from './GeneratedBrief'
import GeneratedTopics from './GeneratedTopics'
import { getSubmitAction } from './helpers'
import ImportTab from './ImportTab'
// import PasteTab from './PasteTab'
import RefreshBriefOverlay from './RefreshBriefOverlay'
import RegenerateModal from './RegenerateModal'

type BriefTabKey = 'brief' | 'import' | 'paste'

export type FormSubmitAction = (
  formValues: Record<string, ConditionalValue<FieldType> | undefined>
) => Promise<void>
export type PageState =
  | 'formChangedWithTopics'
  | 'formChangedWithBrief'
  | 'formChangedWithoutTopics'
  | 'brandVoiceChanged'
  | 'mergeTagsChanged'
  | 'topicsChanged'
  | 'generateBrief'
  | 'generateVariants'
  | 'showGeneratedContentWithoutTopics'
  | 'showGeneratedContent'
  | 'generateWithoutTopics'

type Props = {
  isWebsocketLoading: boolean
}

const BriefPage = ({ isWebsocketLoading }: Props) => {
  const { brazeCampaignImport } = useFlags()
  const dispatch = useAppDispatch()
  const { content } = useContent()
  const { hasVariants } = useCheckVariants()
  const { data: brandVoices } = useGetBrandVoicesQuery()
  const { updateWorkflowName } = useWorkflowName(content)

  const [resetWarningModalState, setResetWarningModalState] = useState<{
    isOpen: boolean
    onConfirm?: () => void
  }>({
    isOpen: false,
    onConfirm: undefined,
  })
  const isModalOpenRef = useRef(false)

  const selectedTopics = useAppSelector(
    (state) => state.unifiedFlow.selectedTopics
  )

  const allTemplateElements = getAllTemplateElements(content)

  const { data: selectedComponentElement } = useSelectedElement()

  const parentTemplateElement = allTemplateElements.find((templateElement) =>
    templateElement.elements.some(
      (element) => selectedComponentElement?.element_id === element.element_id
    )
  )
  const selectedElement = parentTemplateElement || selectedComponentElement

  const { data: { topics, fragments } = {} } = useTopics(
    content?._id,
    selectedElement?.element_id
  )

  const generateVariantsMutation = useGenerateVariantsMutation()

  const isOptimized = getIsOptimized(selectedElement)

  const { isBrazeContent, isLoading: isCheckingBraze } = useIsBrazeContent()
  const isSingleElement = content ? hasOneElement(content.elements) : false
  const showImportPasteTabs = isBrazeContent && isSingleElement

  const existingBrazeCampaignId = (
    selectedElement?.user_input as Record<string, unknown> | undefined
  )?.braze_campaign_id as string | undefined

  const [activeTab, setActiveTab] = useState<BriefTabKey>('brief')

  useEffect(() => {
    if (!showImportPasteTabs && activeTab !== 'brief') {
      setActiveTab('brief')
    }
  }, [showImportPasteTabs, activeTab])

  useEffect(() => {
    if (brazeCampaignImport && showImportPasteTabs && existingBrazeCampaignId) {
      setActiveTab('import')
    }
  }, [brazeCampaignImport, showImportPasteTabs, existingBrazeCampaignId])
  const [hasFormChanged, setHasFormChanged] = useState(false)
  const [hasBrandVoiceChanged, setHasBrandVoiceChanged] = useState(false)
  const [hasMergeTagsChanged, setHasMergeTagsChanged] = useState(false)

  const shouldUseTopics = content?.requires_ner

  const isGlobalSettings = selectedElement === undefined

  const userInputForm = !isGlobalSettings
    ? cloneDeep(
        selectedElement?.user_input?.user_input_form?.filter(
          (field) => field.name !== 'content_name'
        )
      )
    : cloneDeep(content?.user_input.user_input_form)
  const parsedConfiguration = parseFieldsConfiguration(userInputForm)

  const generatedBrief = !isGlobalSettings
    ? selectedElement?.user_input?.generated_brief
    : content?.user_input.generated_brief

  const brandVoiceId =
    content?.elements.length === 1
      ? content.elements[0].user_input?.brand_voice_id
      : selectedElement?.user_input?.brand_voice_id
  const elementMergeTags = isGlobalSettings
    ? content?.elements[0].user_input?.merge_tags?.map(mapBEMergeTag) || []
    : selectedElement?.user_input?.merge_tags?.map(mapBEMergeTag) || []

  const revertTopics = () => {
    dispatch(selectTopics(topics ?? []))
  }

  const hasTopics = topics !== undefined
  const briefStepsStatus = getStepStatus(content?.steps_status, 'brief')
  const isBriefStepsStatusReadOnly = briefStepsStatus === 'read-only'

  const generationStepsStatus = getStepStatus(
    content?.steps_status,
    'generation'
  )
  const isGenerateStepActive =
    generationStepsStatus === 'enabled' || generationStepsStatus === 'active'

  const getState = (): PageState => {
    const hasTopicsChanged = hasTopics && !isEqual(topics, selectedTopics)

    if (hasBrandVoiceChanged && !hasFormChanged && hasVariants) {
      return 'brandVoiceChanged'
    } else if (hasMergeTagsChanged && !hasFormChanged && hasVariants) {
      return 'mergeTagsChanged'
    } else if (hasFormChanged && !shouldUseTopics && hasVariants) {
      return 'formChangedWithoutTopics'
    } else if (hasFormChanged && shouldUseTopics && hasTopics) {
      return 'formChangedWithTopics'
    } else if (hasFormChanged && shouldUseTopics && generatedBrief) {
      return 'formChangedWithBrief'
    } else if (hasTopicsChanged && hasVariants) {
      return 'topicsChanged'
    } else if (!shouldUseTopics && hasVariants) {
      return 'showGeneratedContentWithoutTopics'
    } else if (!shouldUseTopics && !hasVariants && !hasTopics) {
      return 'generateWithoutTopics'
    } else if (!hasVariants && generatedBrief && hasTopics) {
      return 'generateVariants'
    } else if (hasVariants) {
      return 'showGeneratedContent'
    } else {
      return 'generateBrief'
    }
  }

  const state = getState()

  const { generateBrief, generateContentFromImport, generateVariants } =
    useBriefGeneration({
      content,
      selectedElement,
      isGlobalSettings,
      isOptimized,
      shouldUseTopics,
      userInputForm,
      hasVariants,
      isModalOpenRef,
      setResetWarningModalState,
    })

  useEffect(() => {
    isModalOpenRef.current = resetWarningModalState.isOpen
  }, [resetWarningModalState.isOpen])

  useEffect(() => {
    dispatch(selectTopics(topics ?? []))
  }, [dispatch, topics])

  useEffect(() => {
    if (
      (state === 'topicsChanged' ||
        state === 'formChangedWithTopics' ||
        state === 'formChangedWithBrief' ||
        state === 'formChangedWithoutTopics') &&
      !isWebsocketLoading
    ) {
      dispatch(userInputFormHasChanged({ hasChanged: true, isGlobalSettings }))
    } else {
      dispatch(userInputFormHasChanged({ hasChanged: false }))
    }
  }, [dispatch, isGlobalSettings, isWebsocketLoading, state])

  const serializedUserInputForm = JSON.stringify(userInputForm)
  const formInitialValues: any = useMemo(() => {
    const useInputFormInitialValues = userInputForm?.reduce(
      (acc, state) => ({ ...acc, [state.name]: state.value }),
      {}
    )

    const foundBrandVoice = brandVoices?.find(
      (brandVoice) => brandVoice.id === brandVoiceId
    )
    const brandVoice = foundBrandVoice
      ? { value: foundBrandVoice.id, label: foundBrandVoice.name }
      : undefined

    return {
      ...useInputFormInitialValues,
      brand_voice: brandVoice,
      merge_tags: elementMergeTags?.map((mergeTag) => mergeTag.id) || [],
    }
    // UserInputForm might contain properties with array values, that causes the deep comparison to fail and the form to re-initialize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedUserInputForm, brandVoiceId, brandVoices])

  return (
    <div className="flex flex-col flex-1">
      {userInputForm && (
        <Form
          onSubmit={(values) =>
            getSubmitAction(values, generateBrief, generateVariants)(values)
          }
          initialValues={formInitialValues}
          key={selectedElement?.element_id}
          validate={(values) => {
            const errors: ValidationErrors = {}
            const workflowNameValidationError = validateWorkflowName(
              values['workflow_name']
            )

            if (!!workflowNameValidationError) {
              errors.workflow_name = workflowNameValidationError
            }

            return errors
          }}
          render={({ handleSubmit, submitting, dirty, submitSucceeded }) => (
            <form
              onSubmit={handleSubmit}
              onBlur={(e) => {
                if (
                  e.target.name === 'workflow_name' &&
                  e.target.value !== ''
                ) {
                  updateWorkflowName(e.target.value)
                }
              }}
            >
              <FormSpy
                onChange={(formState) => {
                  const dirtyFieldsKeys = Object.keys(formState.dirtyFields)
                  const dirtyFields = dirtyFieldsKeys.filter(
                    (field) =>
                      field !== 'workflow_name' &&
                      field !== 'brand_voice' &&
                      field !== 'merge_tags'
                  )
                  const isDirty = dirtyFields.length > 0
                  setHasFormChanged(isDirty)

                  const brandVoiceChanged =
                    dirtyFieldsKeys.includes('brand_voice') && !isDirty
                  setHasBrandVoiceChanged(brandVoiceChanged)

                  const mergeTagsChanged =
                    dirtyFieldsKeys.includes('merge_tags') && !isDirty
                  setHasMergeTagsChanged(mergeTagsChanged)
                }}
              />
              <div className="flex flex-col">
                {brazeCampaignImport &&
                (showImportPasteTabs || isCheckingBraze) ? (
                  <Tabs<BriefTabKey>
                    variant="inPage"
                    activeKey={activeTab}
                    onTabClick={(key) => setActiveTab(key)}
                    hasContentMargin={false}
                    tabs={[
                      {
                        key: 'brief',
                        name: 'Brief',
                        content: (
                          <>
                            <PageDetails
                              title="Brief"
                              description="Input a short description and Jacquard will generate an optimised brief."
                              className="pb-6 pt-9"
                            />
                            {parsedConfiguration && (
                              <DynamicForm
                                fieldsConfiguration={parsedConfiguration}
                                isDisabled={
                                  submitting || isBriefStepsStatusReadOnly
                                }
                                pageState={state}
                                isLoading={generateVariantsMutation.isPending}
                                brandVoiceId={brandVoiceId}
                                isOptimized={isOptimized}
                              />
                            )}
                          </>
                        ),
                      },
                      {
                        key: 'import' as BriefTabKey,
                        name: 'Import',
                        isDisabled: isCheckingBraze || !showImportPasteTabs,
                        content: (
                          <ImportTab
                            integration="braze"
                            initialCampaignId={existingBrazeCampaignId}
                            onGenerateContent={(id, type, tagIds) =>
                              generateContentFromImport(id, type, tagIds)
                            }
                          />
                        ),
                      },
                    ]}
                  />
                ) : (
                  <>
                    <PageDetails
                      title="Brief"
                      description="Input a short description and Jacquard will generate an optimised brief."
                      className="pb-6"
                    />
                    {parsedConfiguration && (
                      <DynamicForm
                        fieldsConfiguration={parsedConfiguration}
                        isDisabled={submitting || isBriefStepsStatusReadOnly}
                        pageState={state}
                        isLoading={generateVariantsMutation.isPending}
                        brandVoiceId={brandVoiceId}
                        isOptimized={isOptimized}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="relative">
                {state === 'formChangedWithTopics' && <RefreshBriefOverlay />}
                {state === 'brandVoiceChanged' && <ChangesOverlay />}
                {generatedBrief && (
                  <GeneratedBrief
                    brief={generatedBrief}
                    pageState={state}
                    fragments={fragments}
                  />
                )}
                {!(brazeCampaignImport && activeTab === 'import') && (
                  <GeneratedTopics
                    contentId={content?._id}
                    topics={selectedTopics}
                    pageState={state}
                    setSelectedTopics={(topicItems) =>
                      dispatch(selectTopics(topicItems))
                    }
                    revertTopics={revertTopics}
                    isReadOnly={isBriefStepsStatusReadOnly}
                    isGeneratingVariants={generateVariantsMutation.isPending}
                    isGenerateStepActive={isGenerateStepActive}
                  />
                )}
              </div>
              <RouterPromptStay
                shouldShow={
                  !!content &&
                  dirty &&
                  !submitting &&
                  !submitSucceeded &&
                  !isBriefStepsStatusReadOnly
                }
                onOK={() => Promise.resolve(true)}
              />
            </form>
          )}
        />
      )}
      <FormLeaveModal />
      <RegenerateModal
        isOpen={resetWarningModalState.isOpen}
        onConfirm={() => {
          resetWarningModalState.onConfirm?.()
          setResetWarningModalState({ isOpen: false })
        }}
        onClose={() => setResetWarningModalState({ isOpen: false })}
      />
    </div>
  )
}

export default BriefPage
